import { PlanCodes } from 'constants/api/planCodes';
import React, { useEffect, useState } from 'react';
import { MutationResult } from '@apollo/client';
import {
    Box,
    Heading,
    Text,
    Button,
    CheckCircleOutlined,
} from '@landr/maestro';
import { ActivateTrialSubscriptionMutation } from 'apollo/user';
import { Trans } from '@lingui/macro';

type ActivateTrialSubscriptionModalContentProps = {
    onClose(): void;
    onActivateNow(): void;
    loading: MutationResult<
        typeof ActivateTrialSubscriptionMutation
    >['loading'];
    error: MutationResult<typeof ActivateTrialSubscriptionMutation>['error'];
    data: MutationResult<typeof ActivateTrialSubscriptionMutation>['data'];
    trialPlanCode: string;
};

export const ActivateTrialSubscriptionModalContent: React.FC<
    ActivateTrialSubscriptionModalContentProps
> = ({ onClose, onActivateNow, loading, error, data, trialPlanCode }) => {
    const [isSubscriptionActivated, setIsSubscriptionActivated] =
        useState(false);
    const planName = trialPlanCode && PlanCodes[trialPlanCode];

    useEffect(() => {
        if (data) {
            // No point in using the content of data here, since Stripe could take up to 1 hour
            //  to trigger the webhook that will charge the user.
            // However, Stripe documentation states the webhook is triggered "now".
            setIsSubscriptionActivated(!!data);
        }
    }, [data]);

    if (error) {
        return (
            <>
                <Heading as="h5" size="xl" mb={0}>
                    <Trans>Try again</Trans>
                </Heading>
                <Box>
                    <Text contrast="low">
                        <Trans>An error has occurred. Please try again.</Trans>
                    </Text>
                </Box>
                <Box>
                    <Button onClick={onClose} isRounded>
                        <Trans>Got it</Trans>
                    </Button>
                </Box>
            </>
        );
    }

    if (isSubscriptionActivated) {
        return (
            <>
                <CheckCircleOutlined color="primary.300" size={60} />
                <Heading as="h5" size="xl" mb={0}>
                    <Trans>{`You're all set!`}</Trans>
                </Heading>
                <Box>
                    <Text contrast="low">
                        <Trans>
                            You will soon have full access to your plan.
                        </Trans>
                    </Text>
                </Box>
                <Box>
                    <Button onClick={onClose} isLoading={loading} isRounded>
                        <Trans>Got it</Trans>
                    </Button>
                </Box>
            </>
        );
    }

    return (
        <>
            <Heading as="h5" size="xl" mb={0}>
                <Trans>Activate your plan to access this feature</Trans>
            </Heading>
            <Box>
                <Text contrast="low">
                    <Trans>{`You're still on your free 7-day trial. Want to get full ${planName} features? It's easy!`}</Trans>
                </Text>
            </Box>
            <Box>
                <Button onClick={onActivateNow} isLoading={loading} isRounded>
                    <Trans>Activate now</Trans>
                </Button>
            </Box>
        </>
    );
};
